/**
 * Link safety.
 *
 * Project links come from the spreadsheet, which is a document several people
 * could edit, so a value arriving here is not trusted input. Only absolute
 * http and https links are ever opened: a `javascript:` or `data:` URL in a
 * cell would otherwise run when the button was clicked.
 */
export function safeUrl(value: string): string | null {
  const text = value.trim();
  if (!text) return null;
  let parsed: URL;
  try {
    parsed = new URL(text);
  } catch {
    return null;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
  return parsed.href;
}

/** Open a vetted link in a new tab. Returns false when the link is unusable. */
export function openLink(value: string): boolean {
  const url = safeUrl(value);
  if (!url) return false;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

/**
 * Where a project's mindmap lives.
 *
 * The value is either a full http(s) link to a map hosted anywhere, or a bare
 * filename served from this site's own `public/mindmaps/` folder. Supporting
 * both is what lets maps be hosted elsewhere without a repo, while keeping the
 * original filename behaviour working.
 *
 * Returns null when the value is empty or is a link we refuse to open.
 */
export function mindmapUrl(value: string): string | null {
  const text = value.trim();
  if (!text) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(text)) return safeUrl(text);
  // A bare filename. Reject anything trying to climb out of the folder.
  if (text.includes("/") || text.includes("\\") || text.includes("..")) return null;
  return `/mindmaps/${encodeURIComponent(text)}`;
}
